/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import {
  ChannelsService,
  FeProvisioningService,
  MyWanIpService,
  ShowflixCarouselService,
  SmartCarouselService,
  SoftClientCertService,
  InHomeLocationService,
  InternalRatingsService,
  Storage,
  TChannels
} from "@tivocorp/data-domain-lib";
import type {
  Carousel,
  Carousels,
  FeatureResponse,
  FeatureSearchResponse,
  InHomeLocation,
  IpAddress,
  SoftClientCert,
  ShowflixCarousel,
  ShowflixCarousels,
  ShowflixScreensResponse,
  TMyWanIpFetchIpAddressParams,
  TShowflixCarouselCarouselGetParams,
  TShowflixCarouselScreenGetParams,
  TShowflixCarouselScreensNavGetParams,
  TSmartCarouselCarouselGetParams,
  TSmartCarouselScreenGetParams,
  TSmartCarouselLiveTvGetParams,
  TSoftClientCertFetchParams,
  TInHomeLocationFetchParams,
  TInternalRatingsFetchInternalRatingsParams,
  TNpvrAllowRecording,
  TServicesParams,
  StringOverrides
} from "@tivocorp/data-domain-lib";

import type { IPostSigninInteractor } from "../interfaces";
import { BrandingInteractor } from "./shared/BrandingInteractor";
import {
  buildFeProvisioningServiceError,
  EmptyResponseError,
  ErrorTypes,
  FeProvisioningServiceError,
  PartialResponseError
} from "../error";
import {
  Log,
  DiagnosticLogger,
  emitLogControlStatus,
  LoggingWindow,
  PostSigninApi,
  PostSigninLogger,
  ServiceApiLogging,
  WhinyLogging
} from "../logging";
import { Sage, type SageDiagnosticLogLevel } from "../logging/Sage";
import { InternalRatingsLogger } from "../logging/InternalRatingsLogger";
import {
  SoftClientCertFetchReason,
  SoftClientCertLogger,
  SoftClientCertValiditySource,
  type SoftClientCertFetchReasonName
} from "../logging/SoftClientCertLogger";
import type { TLoggingConfigSnapshot } from "../logging/LogControlStatus";
import { PendingTelemetryFlusher } from "../logging/PendingTelemetryFlusher";
import { Locale, LocaleKeys } from "../locale";
import { DeviceFeatures, DeviceModel, InHomeLocationModel } from "../device";
import { parseFeatureAttributeBoolean } from "../device";
import {
  WebSocketManager,
  WebSocketConnectionHolder,
  ConnectionTrigger
} from "../websocket";
import type { IWebSocketConfig } from "../websocket";
import { WebSocketPushNotificationSource } from "../pushNotification";
import { ApplicationFeatureArea } from "../enums";
import { TIVO_SERVICES } from "../../config";
import { BaseInteractor } from "./base";
import { ServiceLocator } from "./base/ServiceLocator";
import { RetryMechanism } from "../retrymechanism";
import { DiagnosticSeverity, RETRY_POLICY } from "../types";
import type { PostSigninApiName, PostSigninLogFields } from "../logging";
import type { TRetryOptions } from "../types";
import type { TNetworkOptions } from "../types/TNetworkOptions";

/**
 * Facade class that wraps SmartCarouselService behind a single
 * entry point for post-sign-in carousel operations.
 *
 * Service instances are **not** stored as class members. A fresh
 * instance is created on every method call so the interactor holds
 * no mutable state. TServicesParams is built internally; callers
 * do not supply it.
 */
export class PostSigninInteractor
  extends BaseInteractor
  implements IPostSigninInteractor
{
  private static TAG: string = "PostSigninInteractor";

  /** Device-features feature name carrying the Whiny logging window. */
  private static readonly WHINY_LOGGING_FEATURE: string = "whinyLogging";

  /** Device-features feature name carrying the Service-API logging window. */
  private static readonly SERVICE_API_LOGGING_FEATURE: string =
    "serviceAPILogging";

  /** Legacy wire name still returned by some device-feature environments. */
  private static readonly SERVICE_API_LOGGING_FEATURE_ALIAS: string =
    "serviceApiLogging";

  /** Remove this flag and fallback after the backend migrates to per-feature windows (UNITY-2117). */
  private static readonly PARSE_FORCE_BACKHAUL_AGGREGATED_WINDOWS: boolean = true;

  private static readonly FORCE_BACKHAUL_FEATURE: string = "forceBackhaul";

  private static readonly FORCE_BACKHAUL_WINDOW_KEYS: Readonly<
    Partial<Record<string, string>>
  > = {
    diagnosticLogging: "diagnosticLogging",
    whinyLogging: "whinyLogging",
    serviceAPILogging: "serviceApiLogging"
  };

  private static readonly FEATURE_ANALYTICS_LOGGING = "analyticsLogging";

  private static readonly FEATURE_DIAGNOSTIC_LOGGING = "diagnosticLogging";

  private static readonly FEATURE_OPERATIONAL_LOGGING = "operationalLogging";

  private static readonly ATTRIBUTE_BUDGET_BYTES = "budgetBytes";

  private static readonly ATTRIBUTE_BUDGET_PERIOD_SEC = "budgetPeriodSec";

  private static readonly ATTRIBUTE_ENABLED = "enabled";

  private static readonly ATTRIBUTE_LOGGING_WINDOW = "loggingWindow";

  private static readonly ATTRIBUTE_LOG_LEVEL = "logLevel";

  private static readonly ATTRIBUTE_BLACKLIST = "blacklist";

  private static readonly ATTRIBUTE_BLACKLIST_VERSION = "blacklistVersion";

  private static readonly DEFAULT_ANALYTICS_ENABLED = true;

  private static readonly DEFAULT_DIAGNOSTIC_LOG_LEVEL: SageDiagnosticLogLevel =
    "WARN";

  private static readonly DIAGNOSTIC_LOG_LEVELS: Record<
    string,
    SageDiagnosticLogLevel
  > = {
    TRACE: "TRACE",
    DEBUG: "DEBUG",
    INFO: "INFO",
    WARN: "WARN",
    ERROR: "ERROR"
  };

  private static readonly DEFAULT_DIAG_BUDGET_BYTES = 33554432; // 32 MB

  private static readonly DEFAULT_DIAG_BUDGET_PERIOD_SEC = 28800; // 8 hours

  private static readonly DEFAULT_OPS_BUDGET_BYTES = 209715200; // 200 MB

  private static readonly DEFAULT_OPS_BUDGET_PERIOD_SEC = 86400; // 24 hours

  /**
   * Default certificate time-to-live: 24 hours in milliseconds.
   * Used when startTime/endTime cannot be derived from the X.509 certificate.
   */
  private static readonly CERT_TTL_MS: number = 86400000;

  /**
   * @param describeFailure - Extra fields for a failed call, e.g. the
   *   catalog error code the failure maps to
   */
  private async executeLoggedPostSigninApi<T>(
    api: PostSigninApiName,
    call: () => Promise<T>,
    describeFailure?: (error: unknown) => PostSigninLogFields
  ): Promise<T> {
    const startTimeMs: number = Date.now();
    try {
      const response: T = await call();
      PostSigninLogger.logApiSuccess(api, Date.now() - startTimeMs);
      return response;
    } catch (error: unknown) {
      PostSigninLogger.logApiFailure(
        api,
        error,
        DiagnosticSeverity.Error,
        Date.now() - startTimeMs,
        describeFailure !== undefined ? describeFailure(error) : undefined
      );
      throw error;
    }
  }

  /**
   * Push-notification receive stage for the current WebSocketManager.
   *
   * Owned here because it must be re-created per manager instance:
   * WebSocketManager.destroy() clears the handler registry, so a source
   * bound to a destroyed manager silently stops receiving notifications.
   */
  private mPushNotificationSource: WebSocketPushNotificationSource | null =
    null;

  /**
   * TSRI catalog key under which the my-wan-ip endpoint is registered.
   * The shared HTTPS-insecure-XFF gateway fronts the my-wan-ip service
   * (hostname pattern `api-my-wan-ip-*.tivoservice.com`), so the TSRI
   * service-name lookup uses this gateway key rather than `"my-wan-ip"`.
   */
  public async fetchCarousel(
    params: TSmartCarouselCarouselGetParams,
    options?: TNetworkOptions
  ): Promise<Carousel> {
    return this.executeGuarded(
      "fetchCarousel",
      async (guardedSignal: AbortSignal): Promise<Carousel> => {
        Log.d(PostSigninInteractor.TAG, "fetchCarousel() called");
        const service: SmartCarouselService = new SmartCarouselService(
          await this.buildServicesParams(
            TIVO_SERVICES.SmartCarousel,
            ApplicationFeatureArea.HOME
          )
        );
        return service.fetchCarousel(params, guardedSignal);
      },
      options
    );
  }

  public async fetchScreen(
    params: TSmartCarouselScreenGetParams,
    options?: TNetworkOptions
  ): Promise<Carousels> {
    return this.executeGuarded(
      "fetchScreen",
      async (guardedSignal: AbortSignal): Promise<Carousels> => {
        Log.d(PostSigninInteractor.TAG, "fetchScreen() called");
        const service: SmartCarouselService = new SmartCarouselService(
          await this.buildServicesParams(
            TIVO_SERVICES.SmartCarousel,
            ApplicationFeatureArea.HOME
          )
        );
        return service.fetchScreen(params, guardedSignal);
      },
      options
    );
  }

  public async fetchLiveTv(
    params: TSmartCarouselLiveTvGetParams,
    options?: TNetworkOptions
  ): Promise<Carousels> {
    return this.executeGuarded(
      "fetchLiveTv",
      async (guardedSignal: AbortSignal): Promise<Carousels> => {
        Log.d(PostSigninInteractor.TAG, "fetchLiveTv() called");
        const service: SmartCarouselService = new SmartCarouselService(
          await this.buildServicesParams(
            TIVO_SERVICES.SmartCarousel,
            ApplicationFeatureArea.HOME
          )
        );
        return service.fetchLiveTv(params, guardedSignal);
      },
      options
    );
  }

  public async fetchShowflixScreen(
    params: TShowflixCarouselScreenGetParams,
    options?: TNetworkOptions,
    featureArea: ApplicationFeatureArea = ApplicationFeatureArea.SHOWS_AND_MOVIES
  ): Promise<ShowflixCarousels> {
    return this.executeGuarded(
      "fetchShowflixScreen",
      async (guardedSignal: AbortSignal): Promise<ShowflixCarousels> => {
        const service = new ShowflixCarouselService(
          await this.buildServicesParams(
            TIVO_SERVICES.ShowflixCarousel,
            featureArea
          )
        );
        return service.fetchScreen(params, guardedSignal);
      },
      options
    );
  }

  public async fetchShowflixCarousel(
    params: TShowflixCarouselCarouselGetParams,
    options?: TNetworkOptions,
    featureArea: ApplicationFeatureArea = ApplicationFeatureArea.SHOWS_AND_MOVIES
  ): Promise<ShowflixCarousel> {
    return this.executeGuarded(
      "fetchShowflixCarousel",
      async (guardedSignal: AbortSignal): Promise<ShowflixCarousel> => {
        const service = new ShowflixCarouselService(
          await this.buildServicesParams(
            TIVO_SERVICES.ShowflixCarousel,
            featureArea
          )
        );
        return service.fetchCarousel(params, guardedSignal);
      },
      options
    );
  }

  public async fetchShowflixScreensNav(
    params: TShowflixCarouselScreensNavGetParams,
    options?: TNetworkOptions,
    featureArea: ApplicationFeatureArea = ApplicationFeatureArea.HOME
  ): Promise<ShowflixScreensResponse> {
    return this.executeGuarded(
      "fetchShowflixScreensNav",
      async (guardedSignal: AbortSignal): Promise<ShowflixScreensResponse> => {
        const service = new ShowflixCarouselService(
          await this.buildServicesParams(
            TIVO_SERVICES.ShowflixCarousel,
            featureArea
          )
        );
        return service.fetchScreensNav(params, guardedSignal);
      },
      options
    );
  }

  public async fetchWanIp(
    params: TMyWanIpFetchIpAddressParams,
    options?: TNetworkOptions
  ): Promise<IpAddress> {
    return this.executeGuarded(
      "fetchWanIp",
      async (guardedSignal: AbortSignal): Promise<IpAddress> => {
        Log.d(PostSigninInteractor.TAG, "fetchWanIp() called");
        const service: MyWanIpService = new MyWanIpService(
          await this.buildServicesParams(
            TIVO_SERVICES.HttpsInsecureXff,
            ApplicationFeatureArea.LOGIN
          )
        );
        return this.executeLoggedPostSigninApi(PostSigninApi.WanIpAddress, () =>
          service.fetchMyWanIpAddress(params, guardedSignal)
        );
      },
      options
    );
  }

  public async fetchInHomeLocation(
    params: TInHomeLocationFetchParams,
    options?: TNetworkOptions
  ): Promise<InHomeLocation> {
    return this.executeGuarded(
      "fetchInHomeLocation",
      async (guardedSignal: AbortSignal): Promise<InHomeLocation> => {
        Log.d(PostSigninInteractor.TAG, "fetchInHomeLocation() called");
        const service: InHomeLocationService = new InHomeLocationService(
          await this.buildServicesParams(
            TIVO_SERVICES.CloudcoreInHomeLocation,
            ApplicationFeatureArea.LOGIN
          )
        );
        const result = await this.executeLoggedPostSigninApi(
          PostSigninApi.InHomeLocation,
          () => service.fetchInHomeLocation(params, guardedSignal)
        );
        // Typed non-nullable by the data layer, but guarded anyway — the
        // service can omit these fields on a partial response.
        const inHome = result.inHome === undefined ? null : result.inHome;
        const inRegion = result.inRegion === undefined ? null : result.inRegion;
        const inBlacklist =
          result.inBlacklist === undefined ? null : result.inBlacklist;
        InHomeLocationModel.set({ inHome, inRegion, inBlacklist });
        return result;
      },
      options
    );
  }

  public async recoverInHomeLocationInfo(
    signal?: AbortSignal
  ): Promise<boolean> {
    try {
      Log.d(PostSigninInteractor.TAG, "recoverInHomeLocationInfo() called");

      const msoPartnerId: string = await DeviceModel.getMsoPartnerId();
      const params: TInHomeLocationFetchParams = {
        msoPartnerId: msoPartnerId
      };

      const retryOptions: TRetryOptions = {
        retryPolicy: RETRY_POLICY.CRITICAL,
        httpMethod: "GET"
      };

      const fetchFn = async (): Promise<InHomeLocation> => {
        return this.fetchInHomeLocation(params);
      };

      await RetryMechanism.callServiceWithRetry<InHomeLocation>(
        fetchFn,
        [],
        retryOptions,
        signal
      );

      return true;
    } catch (error: unknown) {
      Log.e(PostSigninInteractor.TAG, "recoverInHomeLocationInfo()", error);
      return false;
    }
  }

  /**
   * Loads the device feature set from the FeProvisioning gateway.
   * API: GET /v2/deviceFeatures
   *
   * Sends an empty filter body to retrieve all features for the current
   * device. The underlying FeProvisioningService caches the returned
   * features in its static map for later lookup via
   * getDeviceFeatureByName.
   *
   * @param {TNetworkOptions} [options] - Per-call network options (signal, retry)
   * @returns {Promise<FeatureSearchResponse>} Promise resolving to device feature search response
   */
  public async loadDeviceFeatures(
    options?: TNetworkOptions
  ): Promise<FeatureSearchResponse> {
    return this.executeGuarded(
      "loadDeviceFeatures",
      async (guardedSignal: AbortSignal): Promise<FeatureSearchResponse> => {
        Log.d(PostSigninInteractor.TAG, "loadDeviceFeatures() called");
        const service: FeProvisioningService = ServiceLocator.getInstance(
          FeProvisioningService,
          await this.buildServicesParams(
            TIVO_SERVICES.FeatureProvisioningGateway,
            ApplicationFeatureArea.LOGIN
          )
        );

        try {
          const response: FeatureSearchResponse =
            await this.executeLoggedPostSigninApi(
              PostSigninApi.DeviceFeatures,
              () => service.fetchDeviceFeaturesByAttributes({}, guardedSignal)
            );
          PostSigninInteractor.validateFeProvisioningResponse(response);
          PostSigninInteractor.initMsoServiceId(service);
          PostSigninInteractor.initPlaybackConfig(service);
          PostSigninInteractor.initInactivityTime(service);
          DeviceFeatures.initServiceFeatures(response.features);

          const whinyRawWindow: string | null =
            PostSigninInteractor.readLoggingWindowFromSnapshot(
              response,
              PostSigninInteractor.WHINY_LOGGING_FEATURE
            );
          const serviceApiRawWindow: string | null =
            PostSigninInteractor.readLoggingWindowFromSnapshot(
              response,
              PostSigninInteractor.SERVICE_API_LOGGING_FEATURE
            );

          let loggingSnapshot: TLoggingConfigSnapshot | null = null;
          try {
            loggingSnapshot =
              PostSigninInteractor.applySageLoggingConfigFromFeatures(
                service,
                response,
                whinyRawWindow,
                serviceApiRawWindow
              );
          } catch (loggingConfigError: unknown) {
            Log.w(
              PostSigninInteractor.TAG,
              "loadDeviceFeatures() — failed to apply logging config to Sage",
              loggingConfigError
            );
          }

          if (loggingSnapshot !== null) {
            await WhinyLogging.applyFromRawWindow(whinyRawWindow);
            await ServiceApiLogging.applyFromRawWindow(serviceApiRawWindow);
            Sage.setAnalyticsEnabled(loggingSnapshot.analyticsEnabled);
            emitLogControlStatus(loggingSnapshot);
          }

          // Secondary flush trigger — catches any pending telemetry left over
          // from a failed first flush in storeProvisionDeviceData().
          PendingTelemetryFlusher.flush().catch((flushError: unknown) => {
            Log.w(
              PostSigninInteractor.TAG,
              "loadDeviceFeatures() — pending log flush failed",
              flushError
            );
          });

          return response;
        } catch (error: unknown) {
          if (
            error instanceof EmptyResponseError ||
            error instanceof PartialResponseError
          ) {
            Log.e(
              PostSigninInteractor.TAG,
              "loadDeviceFeatures() validation failed",
              error
            );
            throw error;
          }
          const serviceError: FeProvisioningServiceError =
            buildFeProvisioningServiceError(error);
          Log.e(
            PostSigninInteractor.TAG,
            "loadDeviceFeatures() failed",
            serviceError
          );
          throw serviceError;
        }
      },
      options
    );
  }

  /**
   * Fetches the X509 certificate and RSA private key for the device.
   *
   * Uses a cache-first strategy (REQ-CERT-03 / REQ-WEB-CERT-03):
   * 1. If a valid, non-expired certificate exists in IndexedDB and the
   *    bodyId matches, the cached certificate is returned without a
   *    network request.
   * 2. Otherwise, stale data is cleared and a fresh certificate is
   *    acquired from `POST /v1/softClientCert`, persisted to IndexedDB,
   *    and returned.
   *
   * @param {TSoftClientCertFetchParams} params - Request parameters
   * @param {TNetworkOptions} [options] - Per-call network options (signal, retry)
   * @returns {Promise<SoftClientCert>} Promise resolving to soft client cert
   */
  public async fetchSoftClientCert(
    params: TSoftClientCertFetchParams,
    options?: TNetworkOptions
  ): Promise<SoftClientCert> {
    return this.executeGuarded(
      "fetchSoftClientCert",
      async (guardedSignal: AbortSignal): Promise<SoftClientCert> => {
        Log.d(PostSigninInteractor.TAG, "fetchSoftClientCert() called");

        const isValid: boolean = await DeviceModel.isSoftClientCertValid();
        let reason: SoftClientCertFetchReasonName =
          SoftClientCertFetchReason.NotCached;
        if (isValid) {
          const cached = await DeviceModel.getSoftClientCert();
          if (cached !== null) {
            Log.d(
              PostSigninInteractor.TAG,
              "fetchSoftClientCert(): Returning cached certificate"
            );
            SoftClientCertLogger.logCertificateCached(cached.endTime);
            return {
              certificate: cached.certificate,
              privateKey: cached.privateKey
            };
          }
          reason = SoftClientCertFetchReason.KeyMissing;
        }

        await DeviceModel.clearSoftClientCert();

        const service: SoftClientCertService = new SoftClientCertService(
          await this.buildServicesParams(
            TIVO_SERVICES.SoftClientCert,
            ApplicationFeatureArea.LOGIN
          )
        );
        const result: SoftClientCert = await this.executeLoggedPostSigninApi(
          PostSigninApi.SoftClientCert,
          () => service.fetchSoftClientCert(params, guardedSignal),
          (error: unknown) => SoftClientCertLogger.getFailureFields(error)
        );
        await this.persistSoftClientCert(result, reason);

        return result;
      },
      options
    );
  }

  /**
   * Persists a freshly fetched soft client certificate, deriving its validity
   * window from the response or the default TTL. A response without a
   * certificate or private key is reported and not stored.
   */
  private async persistSoftClientCert(
    result: SoftClientCert,
    reason: SoftClientCertFetchReasonName
  ): Promise<void> {
    const { certificate, privateKey } = result;
    if (!certificate || !privateKey) {
      const missingFields: string[] = [];
      if (!certificate) missingFields.push("certificate");
      if (!privateKey) missingFields.push("privateKey");
      SoftClientCertLogger.logInvalidResponse(missingFields);
      return;
    }

    const now: number = Date.now();
    const parsedStart: number =
      result.startTime !== undefined
        ? new Date(result.startTime).getTime()
        : NaN;
    const parsedEnd: number =
      result.endTime !== undefined ? new Date(result.endTime).getTime() : NaN;

    const startTime: number = Number.isFinite(parsedStart) ? parsedStart : now;
    const hasServerEndTime: boolean =
      Number.isFinite(parsedEnd) && parsedEnd > now;
    const endTime: number = hasServerEndTime
      ? parsedEnd
      : now + PostSigninInteractor.CERT_TTL_MS;

    try {
      await DeviceModel.storeSoftClientCert(
        certificate,
        privateKey,
        startTime,
        endTime
      );
    } catch (error: unknown) {
      SoftClientCertLogger.logStoreFailed(error);
      return;
    }
    SoftClientCertLogger.logFetchSucceeded(
      reason,
      hasServerEndTime
        ? SoftClientCertValiditySource.Server
        : SoftClientCertValiditySource.DefaultTtl,
      endTime
    );
  }

  /**
   * Fetches internal ratings values and configs.
   * The InternalRatingsService in js-domain-lib handles caching
   * to Storage with a 24-hour TTL internally.
   *
   * Builds the request params (locale, msoPartnerId) internally from
   * DeviceModel — callers do not need to supply them.
   *
   * @param {TNetworkOptions} [options] - Per-call network options (signal, retry)
   */
  public async fetchInternalRatings(options?: TNetworkOptions): Promise<void> {
    return this.executeGuarded(
      "fetchInternalRatings",
      async (guardedSignal: AbortSignal): Promise<void> => {
        Log.d(PostSigninInteractor.TAG, "fetchInternalRatings() called");

        const msoPartnerId: string = await DeviceModel.getMsoPartnerId();
        const locale: string = DeviceModel.getTVProperties().getLocale();
        const params: TInternalRatingsFetchInternalRatingsParams = {
          locale: locale,
          msoPartnerId: msoPartnerId
        };

        const service: InternalRatingsService = new InternalRatingsService(
          await this.buildServicesParams(
            TIVO_SERVICES.CloudcoreRating,
            ApplicationFeatureArea.LOGIN
          )
        );
        await this.executeLoggedPostSigninApi(
          PostSigninApi.InternalRatings,
          () => service.fetchInternalRatings(params, guardedSignal),
          (error: unknown) => InternalRatingsLogger.getFailureFields(error)
        );
        // The service resolves void, so the catalog it resolved is read back
        // from its cache. A failed read is not a failed lookup: the request
        // itself succeeded.
        try {
          InternalRatingsLogger.logRatingsLoaded(
            await service.getInternalRatingsData()
          );
        } catch (error: unknown) {
          Log.w(
            PostSigninInteractor.TAG,
            "fetchInternalRatings() — could not read back cached ratings",
            error
          );
        }
      },
      options
    );
  }

  /**
   * Service params for the internal-ratings endpoint, for callers that drive
   * `InternalRatingsService` themselves rather than through
   * {@link fetchInternalRatings} — the player's info banner resolves rating
   * *names* from the already-cached catalog.
   *
   * Exposed here rather than assembled at the call site so the access token
   * comes from TokenManager along with every other authenticated request.
   */
  public async buildInternalRatingsServicesParams(): Promise<TServicesParams> {
    return this.buildServicesParams(
      TIVO_SERVICES.CloudcoreRating,
      ApplicationFeatureArea.ALL
    );
  }

  public async fetchNpvrAllowRecording(
    options?: TNetworkOptions
  ): Promise<TNpvrAllowRecording> {
    return this.executeGuarded(
      "fetchNpvrAllowRecording",
      async (guardedSignal: AbortSignal): Promise<TNpvrAllowRecording> => {
        Log.d(PostSigninInteractor.TAG, "fetchNpvrAllowRecording() called");
        const service: FeProvisioningService = ServiceLocator.getInstance(
          FeProvisioningService,
          await this.buildServicesParams(
            TIVO_SERVICES.FeatureProvisioningGateway,
            ApplicationFeatureArea.LOGIN
          )
        );
        try {
          return await this.executeLoggedPostSigninApi(
            PostSigninApi.NpvrAllowRecording,
            () => service.fetchNpvrAllowRecording(guardedSignal)
          );
        } catch (error: unknown) {
          // Mapped like loadDeviceFeatures so the failure carries an FE code.
          // The builder re-attaches the response, so executeGuarded still
          // sees a 401 and replays the call with a fresh token.
          throw buildFeProvisioningServiceError(error);
        }
      },
      options
    );
  }

  /**
   * Fetches the post-sign-in string overrides.
   * API: GET /v1/stringOverrides
   *
   * Delegates to {@link BrandingInteractor.fetchStringOverrides} rather than
   * re-implementing the call here: that class already owns the
   * device-configuration-service params (msoName + msoServiceId, no access
   * token — see `BrandingInteractor.buildDeviceConfigServicesParams`), the
   * error handling, and the `PostSigninApi.StringOverrides` logging, and is
   * the same interactor `fetchBrandingUiBundle` (the sibling
   * msoDefaultDeviceConfig-family call) already goes through. Exposed here
   * too so `postSigninStartupSequence` can call it through this single
   * `interactor` instance, the same way it calls every other startup step.
   */
  public async fetchStringOverrides(
    options?: TNetworkOptions
  ): Promise<StringOverrides> {
    return new BrandingInteractor().fetchStringOverrides(options);
  }

  /**
   * Establish the shared WebSocket connection (REQ-FSM-01 through REQ-FSM-08).
   *
   * Creates a WebSocketManager with FSM state-processor delegates so that
   * the FSM drives cert acquisition and endpoint resolution on every
   * connection attempt (including RESTART after errors):
   *
   * - **ensureCertificate** (GET_CLIENT_CERT): validates/fetches the soft
   *   client certificate via `POST /v1/softClientCert`.
   * - **resolveEndpoint** (GET_SECURE_ENDPOINT): resolves the WSS host/port
   *   via TSRI service name `"wssecure"`.
   * - **CHECK_BRIDGEKEEPER**: pass-through — service not available on web.
   * - **ESTABLISH_CONTEXT**: handled internally by WebSocketManager (JWT + connect).
   *
   * @param trigger - The named reason for connection initiation
   */
  public async connectWebSocket(
    trigger: ConnectionTrigger = ConnectionTrigger.PAGE_LOAD
  ): Promise<void> {
    try {
      Log.i(PostSigninInteractor.TAG, "connectWebSocket(): Creating manager");

      // Initial endpoint resolution for config defaults
      const endpoint: string = await this.getServiceEndpointByServiceName(
        TIVO_SERVICES.WsSecure
      );
      const endpointUrl: URL = new URL(endpoint);
      const host: string = endpointUrl.hostname;
      const port: number = endpointUrl.port
        ? parseInt(endpointUrl.port, 10)
        : 443;

      Log.i(
        PostSigninInteractor.TAG,
        `connectWebSocket(): Initial endpoint — host=${host}, port=${port}`
      );

      const config: IWebSocketConfig = {
        host,
        port,

        // FSM GET_CLIENT_CERT delegate — called on every attempt/restart
        ensureCertificate: async (): Promise<void> => {
          Log.d(
            PostSigninInteractor.TAG,
            "ensureCertificate(): Checking certificate validity"
          );
          const isValid: boolean = await DeviceModel.isSoftClientCertValid();
          if (isValid) {
            SoftClientCertLogger.logCertificateCached();
          } else {
            Log.d(
              PostSigninInteractor.TAG,
              "ensureCertificate(): Certificate missing or expired — fetching fresh"
            );
            await this.fetchSoftClientCert({
              xAmznRequestId: DeviceModel.getDeviceUuid()
            });
          }
        },

        // FSM GET_SECURE_ENDPOINT delegate — called on every attempt/restart
        resolveEndpoint: async (): Promise<{ host: string; port: number }> => {
          Log.d(
            PostSigninInteractor.TAG,
            "resolveEndpoint(): Resolving wssecure via TSRI"
          );
          const ep: string = await this.getServiceEndpointByServiceName(
            TIVO_SERVICES.WsSecure
          );
          const url: URL = new URL(ep);
          const resolvedHost: string = url.hostname;
          const resolvedPort: number = url.port ? parseInt(url.port, 10) : 443;
          Log.d(
            PostSigninInteractor.TAG,
            `resolveEndpoint(): Resolved ${resolvedHost}:${resolvedPort}`
          );
          return { host: resolvedHost, port: resolvedPort };
        }
      };

      const manager: WebSocketManager = new WebSocketManager(config);

      // Push-notification receive stage. Registered before start() so an
      // inbound notification cannot arrive ahead of the handler, and
      // re-created per manager because destroy() clears the registry.
      if (this.mPushNotificationSource !== null) {
        this.mPushNotificationSource.destroy();
      }
      this.mPushNotificationSource = new WebSocketPushNotificationSource(
        manager
      );
      this.mPushNotificationSource.startListening();

      // Store the shared instance (destroys any previous)
      WebSocketConnectionHolder.setInstance(manager);

      // Start the connection — FSM drives the full flow
      await this.executeLoggedPostSigninApi(
        PostSigninApi.WebSocketConnection,
        () => manager.start(trigger)
      );

      Log.i(
        PostSigninInteractor.TAG,
        "connectWebSocket(): Connection initiated"
      );
    } catch (error: unknown) {
      Log.e(PostSigninInteractor.TAG, "connectWebSocket()", error);
      throw error;
    }
  }

  /**
   * Drops the cached channel line-up and station policies.
   *
   * Called when the active profile changes. `ChannelsService` serves
   * `fetchAllChannels` from these keys for six hours, keyed on time alone — it
   * has no notion of which profile the line-up was fetched for — so without
   * this {@link fetchChannels} is answered from the previous profile's data
   * and never reaches the service.
   *
   * Dropping the expiry stamp is what forces the refetch: `updateChannelsCache`
   * re-fetches and then rebuilds its static index maps. The payload keys go
   * with it so a failed refetch cannot fall back on another profile's line-up
   * as "stale data".
   *
   * Failures are logged, not thrown — a cache that could not be cleared leaves
   * the previous line-up in place, which is no worse than not trying.
   */
  public async clearChannelsCache(): Promise<void> {
    try {
      Log.d(PostSigninInteractor.TAG, "clearChannelsCache() called");
      const storage: Storage = Storage.getInstance();
      // Resolved here rather than in a static field: `Storage` is a live
      // binding the test stub supplies per suite, so reading `Keys` at class
      // definition time would evaluate it before any mock is in place.
      const cacheKeys: string[] = [
        Storage.Keys.CHANNELS_DATA_LOCAL_CACHE_EXPIRY_TIME,
        Storage.Keys.CHANNELS,
        Storage.Keys.FILTERED_OUT_CHANNELS,
        Storage.Keys.STATION_POLICIES
      ];
      for (const key of cacheKeys) {
        await storage.delete(key);
      }
    } catch (error: unknown) {
      Log.e(PostSigninInteractor.TAG, "clearChannelsCache()", error);
    }
  }

  public async fetchChannels(
    offset?: number,
    limit?: number
  ): Promise<TChannels> {
    try {
      Log.d(PostSigninInteractor.TAG, "fetchChannels() called");
      const channelLogoBaseUrl = await this.getServiceEndpointByServiceName(
        TIVO_SERVICES.ChannelLogoImageHost
      );
      const service: ChannelsService = new ChannelsService(
        await this.buildServicesParams(
          TIVO_SERVICES.ChannelsIptv,
          ApplicationFeatureArea.HOME
        ),
        channelLogoBaseUrl
      );
      return await service.fetchAllChannels(offset, limit);
    } catch (error: unknown) {
      Log.e(PostSigninInteractor.TAG, "fetchChannels()", error);
      throw error;
    }
  }

  /**
   * Extracts `msoServiceId` from the just-cached device features and publishes
   * it on {@link DeviceFeatures}.
   *
   * Throws PartialResponseError when the value cannot be resolved. That is not
   * a cosmetic gap: `msoServiceId` is a required parameter of every
   * msoServiceId-scoped service (the branding UI bundle today), so a device
   * feature response without it leaves those calls to go out with an empty
   * string and be rejected by the service. Failing here instead makes the
   * caller's retry policy the thing that decides what happens, and keeps the
   * cause in one log line rather than surfacing as an unrelated 4xx later.
   *
   * Three distinct shapes all reduce to the same verdict:
   *   - the feature is absent (`getDeviceFeatureByName` throws),
   *   - the feature is present but carries no attributes,
   *   - the attributes carry no `msoServiceIdName` entry.
   * `DeviceFeatures.isMsoServiceIdValid()` is false in every one of them.
   */
  private static initMsoServiceId(service: FeProvisioningService): void {
    try {
      DeviceFeatures.initMsoServiceIdFeature(
        service.getDeviceFeatureByName(DeviceFeatures.MSO_SERVICE_ID)
      );
    } catch (featureError: unknown) {
      Log.e(
        PostSigninInteractor.TAG,
        "loadDeviceFeatures() — msoServiceId feature not found in response",
        featureError
      );
    }

    if (DeviceFeatures.isMsoServiceIdValid()) {
      return;
    }

    throw new PartialResponseError(
      ErrorTypes.FE_PROVISIONING_PARTIAL_RESPONSE_A611,
      "Device feature response carries no msoServiceId",
      Locale.getString(
        LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
      )
    );
  }

  /**
   * Extracts the `playbackConfig` feature from the just-cached device features
   * and publishes its `switchGroup` map on {@link DeviceFeatures}.
   *
   * Absence is tolerated, unlike `msoServiceId`: the feature is optional in the
   * FeProvisioning response, and `DeviceFeatures.getSwitchGroup()` returns an
   * empty string when it is missing, leaving the fallback to the playback
   * caller instead of failing sign-in over it.
   */
  private static initPlaybackConfig(service: FeProvisioningService): void {
    try {
      DeviceFeatures.initPlaybackConfigFeature(
        service.getDeviceFeatureByName(DeviceFeatures.PLAYBACK_CONFIG)
      );
    } catch (featureError: unknown) {
      Log.w(
        PostSigninInteractor.TAG,
        "loadDeviceFeatures() — playbackConfig feature not found in response",
        featureError
      );
    }
  }

  /**
   * Extracts the `inactivityTime` feature from the just-cached device
   * features and publishes its parsed timeout on {@link DeviceFeatures}.
   *
   * Absence is tolerated, unlike `msoServiceId`: `DeviceFeatures.
   * getPlaybackInactivityTimeoutMs()` falls back to the `config.ts` default
   * when this feature is missing, leaving sign-in unaffected.
   */
  private static initInactivityTime(service: FeProvisioningService): void {
    try {
      DeviceFeatures.initInactivityTimeFeature(
        service.getDeviceFeatureByName(DeviceFeatures.INACTIVITY_TIME)
      );
    } catch (featureError: unknown) {
      Log.w(
        PostSigninInteractor.TAG,
        "loadDeviceFeatures() — inactivityTime feature not found in response",
        featureError
      );
    }
  }

  /**
   * Validates the FeProvisioning response. Throws EmptyResponseError when
   * features cannot be demarshalled and PartialResponseError when the
   * features array is empty.
   */
  private static validateFeProvisioningResponse(
    response: FeatureSearchResponse
  ): void {
    const features = response.features;
    if (
      features === undefined ||
      features === null ||
      !Array.isArray(features)
    ) {
      throw new EmptyResponseError(
        ErrorTypes.FE_PROVISIONING_DEMARSHAL_ERROR_A610,
        Locale.getString(LocaleKeys.FE_PROVISIONING_DEMARSHAL_ERROR_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
    if (features.length === 0) {
      throw new PartialResponseError(
        ErrorTypes.FE_PROVISIONING_PARTIAL_RESPONSE_A611,
        Locale.getString(LocaleKeys.FE_PROVISIONING_PARTIAL_RESPONSE_MESSAGE),
        Locale.getString(
          LocaleKeys.TEMPORARY_SERVICE_CONNECTION_ISSUE_DESCRIPTION
        )
      );
    }
  }

  private static applySageLoggingConfigFromFeatures(
    service: FeProvisioningService,
    response: FeatureSearchResponse,
    whinyRawWindow: string | null,
    serviceApiRawWindow: string | null
  ): TLoggingConfigSnapshot {
    const applyNowEpochSec: number = Math.floor(Date.now() / 1000);
    const diagBudgetBytes = PostSigninInteractor.readFeatureAttributeInt(
      service,
      PostSigninInteractor.FEATURE_DIAGNOSTIC_LOGGING,
      PostSigninInteractor.ATTRIBUTE_BUDGET_BYTES,
      PostSigninInteractor.DEFAULT_DIAG_BUDGET_BYTES
    );
    const diagBudgetPeriodSec = PostSigninInteractor.readFeatureAttributeInt(
      service,
      PostSigninInteractor.FEATURE_DIAGNOSTIC_LOGGING,
      PostSigninInteractor.ATTRIBUTE_BUDGET_PERIOD_SEC,
      PostSigninInteractor.DEFAULT_DIAG_BUDGET_PERIOD_SEC
    );
    const opsBudgetBytes = PostSigninInteractor.readFeatureAttributeInt(
      service,
      PostSigninInteractor.FEATURE_OPERATIONAL_LOGGING,
      PostSigninInteractor.ATTRIBUTE_BUDGET_BYTES,
      PostSigninInteractor.DEFAULT_OPS_BUDGET_BYTES
    );
    const opsBudgetPeriodSec = PostSigninInteractor.readFeatureAttributeInt(
      service,
      PostSigninInteractor.FEATURE_OPERATIONAL_LOGGING,
      PostSigninInteractor.ATTRIBUTE_BUDGET_PERIOD_SEC,
      PostSigninInteractor.DEFAULT_OPS_BUDGET_PERIOD_SEC
    );

    Sage.setLoggingBudgets(
      diagBudgetPeriodSec,
      diagBudgetBytes,
      opsBudgetPeriodSec,
      opsBudgetBytes
    );

    const diagnosticWindow = PostSigninInteractor.readLoggingWindowFromSnapshot(
      response,
      PostSigninInteractor.FEATURE_DIAGNOSTIC_LOGGING
    );
    const parsedDiagnosticWindow: LoggingWindow | null =
      LoggingWindow.parse(diagnosticWindow);
    DiagnosticLogger.setDiagnosticLoggingWindow(parsedDiagnosticWindow);

    const isAnalyticsEnabled = PostSigninInteractor.readFeatureAttributeBoolean(
      service,
      PostSigninInteractor.FEATURE_ANALYTICS_LOGGING,
      PostSigninInteractor.ATTRIBUTE_ENABLED,
      PostSigninInteractor.DEFAULT_ANALYTICS_ENABLED
    );

    const diagnosticLogLevel = PostSigninInteractor.normalizeDiagnosticLogLevel(
      PostSigninInteractor.readFeatureAttributeString(
        service,
        PostSigninInteractor.FEATURE_DIAGNOSTIC_LOGGING,
        PostSigninInteractor.ATTRIBUTE_LOG_LEVEL
      )
    );
    const diagnosticBlacklist =
      PostSigninInteractor.normalizeDiagnosticBlacklist(
        PostSigninInteractor.readFeatureAttributeString(
          service,
          PostSigninInteractor.FEATURE_DIAGNOSTIC_LOGGING,
          PostSigninInteractor.ATTRIBUTE_BLACKLIST
        )
      );
    const diagnosticBlacklistVersion =
      PostSigninInteractor.normalizeDiagnosticBlacklistVersion(
        PostSigninInteractor.readFeatureAttributeString(
          service,
          PostSigninInteractor.FEATURE_DIAGNOSTIC_LOGGING,
          PostSigninInteractor.ATTRIBUTE_BLACKLIST_VERSION
        )
      );
    Sage.setLogLevel(diagnosticLogLevel);
    Sage.setBlacklist(diagnosticBlacklist, diagnosticBlacklistVersion);

    const whinyWindow: LoggingWindow | null =
      LoggingWindow.parse(whinyRawWindow);
    const serviceApiWindow: LoggingWindow | null =
      LoggingWindow.parse(serviceApiRawWindow);
    const snapshot: TLoggingConfigSnapshot = {
      analyticsEnabled: isAnalyticsEnabled,
      diagnosticBlacklistSize: diagnosticBlacklist.length,
      diagnosticBlacklistVersion,
      diagnosticBudgetBytes: diagBudgetBytes,
      diagnosticBudgetPeriodSec: diagBudgetPeriodSec,
      diagnosticEnabled:
        parsedDiagnosticWindow !== null &&
        parsedDiagnosticWindow.isInWindow(applyNowEpochSec),
      diagnosticLevel: diagnosticLogLevel,
      diagnosticWindow: parsedDiagnosticWindow,
      operationalBudgetBytes: opsBudgetBytes,
      operationalBudgetPeriodSec: opsBudgetPeriodSec,
      serviceApiEnabled:
        serviceApiWindow !== null &&
        serviceApiWindow.isInWindow(applyNowEpochSec),
      serviceApiWindow,
      whinyEnabled:
        whinyWindow !== null && whinyWindow.isInWindow(applyNowEpochSec),
      whinyWindow
    };

    return snapshot;
  }

  private static normalizeDiagnosticLogLevel(
    value: string | null
  ): SageDiagnosticLogLevel {
    if (value === null) {
      return PostSigninInteractor.DEFAULT_DIAGNOSTIC_LOG_LEVEL;
    }

    const normalizedValue = value.trim().toUpperCase();
    const diagnosticLogLevel =
      PostSigninInteractor.DIAGNOSTIC_LOG_LEVELS[normalizedValue];
    if (diagnosticLogLevel === undefined) {
      return PostSigninInteractor.DEFAULT_DIAGNOSTIC_LOG_LEVEL;
    }
    return diagnosticLogLevel;
  }

  private static normalizeDiagnosticBlacklist(value: string | null): string[] {
    if (value === null) {
      return [];
    }

    const blacklist = new Set<string>();
    value.split(",").forEach((entry: string): void => {
      const trimmedEntry = entry.trim();
      if (trimmedEntry !== "") {
        blacklist.add(trimmedEntry);
      }
    });
    return Array.from(blacklist);
  }

  private static normalizeDiagnosticBlacklistVersion(
    value: string | null
  ): string | undefined {
    if (value === null) {
      return undefined;
    }

    const normalizedValue = value.trim();
    return normalizedValue === "" ? undefined : normalizedValue;
  }

  private static readFeatureAttributeBoolean(
    service: FeProvisioningService,
    featureName: string,
    attributeName: string,
    fallbackValue: boolean
  ): boolean {
    try {
      const feature = service.getDeviceFeatureByName(featureName);
      return parseFeatureAttributeBoolean(
        feature,
        attributeName,
        fallbackValue
      );
    } catch {
      return fallbackValue;
    }
  }

  private static readFeatureAttributeInt(
    service: FeProvisioningService,
    featureName: string,
    attributeName: string,
    fallbackValue: number
  ): number {
    type FeatureAttribute = {
      attributeName?: string;
      attributeValue?: string;
    };
    type FeatureWithAttributes = {
      attributes?: FeatureAttribute[];
    };

    try {
      const feature = service.getDeviceFeatureByName(
        featureName
      ) as FeatureWithAttributes;
      const attributes = feature.attributes;
      if (!Array.isArray(attributes)) {
        return fallbackValue;
      }

      const attribute = attributes.find(
        (item) => item.attributeName === attributeName
      );
      if (attribute === undefined || attribute.attributeValue === undefined) {
        return fallbackValue;
      }

      const parsed = Number.parseInt(attribute.attributeValue.trim(), 10);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return fallbackValue;
      }

      return parsed;
    } catch {
      return fallbackValue;
    }
  }

  private static readLoggingWindowFromSnapshot(
    response: FeatureSearchResponse,
    featureName: string
  ): string | null {
    const featureNames: string[] =
      featureName === PostSigninInteractor.SERVICE_API_LOGGING_FEATURE
        ? [
            PostSigninInteractor.SERVICE_API_LOGGING_FEATURE,
            PostSigninInteractor.SERVICE_API_LOGGING_FEATURE_ALIAS
          ]
        : [featureName];
    const perFeatureWindow =
      PostSigninInteractor.readWindowAttributeFromSnapshot(
        response,
        featureNames,
        featureNames
      );
    if (
      perFeatureWindow !== null ||
      !PostSigninInteractor.PARSE_FORCE_BACKHAUL_AGGREGATED_WINDOWS
    ) {
      return perFeatureWindow;
    }

    const legacyKey =
      PostSigninInteractor.FORCE_BACKHAUL_WINDOW_KEYS[featureName];
    if (legacyKey === undefined) {
      return null;
    }
    return PostSigninInteractor.readWindowAttributeFromSnapshot(
      response,
      [PostSigninInteractor.FORCE_BACKHAUL_FEATURE],
      [legacyKey]
    );
  }

  private static readWindowAttributeFromSnapshot(
    response: FeatureSearchResponse,
    featureNames: string[],
    windowKeys: string[]
  ): string | null {
    const features: FeatureResponse[] | undefined = response.features;
    if (!Array.isArray(features)) {
      return null;
    }

    const feature: FeatureResponse | undefined = features.find(
      (candidate: FeatureResponse): boolean =>
        candidate !== null &&
        typeof candidate === "object" &&
        candidate.featureName !== undefined &&
        featureNames.includes(candidate.featureName)
    );
    if (feature === undefined || !Array.isArray(feature.attributes)) {
      return null;
    }

    const loggingWindowAttribute = feature.attributes.find(
      (attribute): boolean =>
        attribute !== null &&
        typeof attribute === "object" &&
        attribute.attributeName ===
          PostSigninInteractor.ATTRIBUTE_LOGGING_WINDOW
    );
    if (
      loggingWindowAttribute === undefined ||
      typeof loggingWindowAttribute.attributeValue !== "string"
    ) {
      return null;
    }

    const rawWindow = PostSigninInteractor.extractLoggingWindowValue(
      loggingWindowAttribute.attributeValue,
      windowKeys
    );
    return LoggingWindow.parse(rawWindow) === null ? null : rawWindow;
  }

  private static extractLoggingWindowValue(
    rawWindowJson: string,
    featureNames: string[]
  ): string | null {
    try {
      const parsed: unknown = JSON.parse(rawWindowJson) as unknown;
      if (
        parsed === null ||
        typeof parsed !== "object" ||
        Array.isArray(parsed)
      ) {
        return null;
      }

      for (const featureName of featureNames) {
        const value = (parsed as Record<string, unknown>)[featureName];
        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed !== "") {
            return trimmed;
          }
        }
      }
      return null;
    } catch {
      return null;
    }
  }

  private static readFeatureAttributeString(
    service: FeProvisioningService,
    featureName: string,
    attributeName: string
  ): string | null {
    type FeatureAttribute = {
      attributeName?: string;
      attributeValue?: string;
    };
    type FeatureWithAttributes = {
      attributes?: FeatureAttribute[];
    };

    try {
      const feature = service.getDeviceFeatureByName(
        featureName
      ) as FeatureWithAttributes;
      const attributes = feature.attributes;
      if (!Array.isArray(attributes)) {
        return null;
      }

      const attribute = attributes.find(
        (item) => item.attributeName === attributeName
      );
      if (attribute === undefined || attribute.attributeValue === undefined) {
        return null;
      }

      const trimmed = attribute.attributeValue.trim();
      return trimmed === "" ? null : trimmed;
    } catch {
      return null;
    }
  }
}
